/**
 * Search CRM Handler
 * Search handler for full-text search across CRM entities using GraphQL
 */

import type { ToolExecutionContext } from '../../types';
import { QueryHandlerBase } from '../base';
import { stringField, arrayField, numberField } from '../../utils/schema-builder';
import { createUserGraphQLClient } from '../../utils/graphql-client';
import { logger } from '../../../logger';

/**
 * Search CRM handler
 * Uses GraphQL queries to search across contacts and companies with RLS enforcement
 */
export class SearchCRMHandler extends QueryHandlerBase<any> {
  id = 'search_crm';

  schema = {
    type: 'object',
    properties: {
      query: stringField('Search query'),
      entity_types: arrayField('Entity types to search (contacts, companies, deals, etc.)', {
        type: 'string',
      }),
      limit: numberField('Maximum number of results (default: 20)', { minimum: 1, maximum: 100 }),
    },
    required: ['query'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<any> {
    const limit = params.limit || 20;
    const entityTypes = params.entity_types || ['contacts', 'companies'];
    const searchQuery = params.query;

    if (!context?.userToken) {
      throw new Error('User token is required for CRM search');
    }

    try {
      // Dynamically import GraphQL types and documents to avoid circular dependencies
      const { GetContactssDocument, GetCompaniessDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);
      const results = [];

      // Search contacts
      if (entityTypes.includes('contacts')) {
        const contactResult = await client.query({
          query: GetContactssDocument,
          variables: {
            first: limit,
            filter: {
              or: [
                { name: { ilike: `%${searchQuery}%` } },
                { email: { ilike: `%${searchQuery}%` } },
                { phone: { ilike: `%${searchQuery}%` } },
              ],
            },
          },
          fetchPolicy: 'network-only',
        });

        if (!contactResult.error) {
          const contactData = contactResult.data as any;
          if (contactData?.contactsCollection?.edges) {
            const contacts = contactData.contactsCollection.edges
              .map((edge: any) => edge?.node)
              .filter((node: any) => node != null);
            results.push(
              ...contacts.map((contact: any) => ({
                type: 'contact',
                ...contact,
              }))
            );
          }
        } else {
          logger.error('contact search error', contactResult.error, { op: 'SearchCRMHandler' });
        }
      }

      // Search companies
      if (entityTypes.includes('companies')) {
        const companyResult = await client.query({
          query: GetCompaniessDocument,
          variables: {
            first: limit,
            filter: {
              or: [
                { name: { ilike: `%${searchQuery}%` } },
                { industry: { ilike: `%${searchQuery}%` } },
                { website: { ilike: `%${searchQuery}%` } },
              ],
            },
          },
          fetchPolicy: 'network-only',
        });

        if (!companyResult.error) {
          const companyData = companyResult.data as any;
          if (companyData?.companiesCollection?.edges) {
            const companies = companyData.companiesCollection.edges
              .map((edge: any) => edge?.node)
              .filter((node: any) => node != null);
            results.push(
              ...companies.map((company: any) => ({
                type: 'company',
                ...company,
              }))
            );
          }
        } else {
          logger.error('company search error', companyResult.error, { op: 'SearchCRMHandler' });
        }
      }

      if (results.length === 0) {
        return {
          query: searchQuery,
          results: [],
          total: 0,
          message: `No results found for "${searchQuery}"`,
        };
      }

      const withLinks = results.slice(0, limit).map((r: any) => ({
        ...r,
        viewInApp: r.type === 'contact' ? `[${r.name || 'Contact'}](/contacts/${r.id})` : `[${r.name || 'Company'}](/companies/${r.id})`,
      }));
      return {
        query: searchQuery,
        results: withLinks,
        total: results.length,
        hasMore: results.length > limit,
        viewInApp: withLinks.map((r: any) => r.viewInApp).join(' · '),
      };
    } catch (error) {
      throw new Error(`CRM search failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
